import { Check, Copy, Link2, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import { FeaturedSession } from "~/services/projects";
import type { UseConversationShare } from "./useConversationShare";
import { useFeaturedToggle } from "./useFeaturedToggle";

interface ShareSessionBodyProps {
  share: UseConversationShare;
  projectId: string;
  // Whether the caller owns the project — gates the "Add to Featured Sessions" toggle.
  isOwner: boolean;
  // Needed to feature the session: the rail stores a name and agent snapshot,
  // not a reference back to the conversation.
  displayName: string;
  agentId?: string | null;
  featuredSessions: FeaturedSession[] | undefined;
}

// Renders the correct share-link surface for the current state, plus (for
// project owners) the "Featured Session" toggle below it. A featured session
// requires a share link to exist — the two are coupled, not independent
// settings — so featuring on generates a link if one doesn't already exist
// (the create endpoint is idempotent, so this never duplicates an existing
// link), and revoking a link that's currently featured un-features it too.
// The backend itself doesn't cascade this — the projects service has no way
// to know a share was revoked — so this is two sequential client calls
// (revoke, then unfeature) rather than one atomic operation. "Revoke link"
// sits in its own bottom-right row, separate from the featuring action, since
// it's the more consequential of the two. Shared by the centered
// ShareConversationDialog and the inline composer SharePopover.
export const ShareSessionBody = ({
  share,
  projectId,
  isOwner,
  displayName,
  agentId,
  featuredSessions,
}: ShareSessionBodyProps) => {
  const {
    share: shareState,
    shareUrl,
    isMutating,
    copied,
    handleGenerate,
    handleRevoke,
    handleCopy,
  } = share;

  const featured = useFeaturedToggle(
    projectId,
    featuredSessions,
    shareState?.token,
    displayName,
    agentId
  );

  const handleFeaturedToggle = async (checked: boolean) => {
    if (checked) {
      const created = await handleGenerate();
      if (!created) {
        return;
      }
      await featured.handleToggle(checked, created.token);
      return;
    }
    await featured.handleToggle(checked);
  };

  const handleRevokeAndUnfeature = async () => {
    const wasFeatured = featured.isFeatured;
    const revoked = await handleRevoke();
    if (revoked && wasFeatured) {
      await featured.handleToggle(false);
    }
  };

  const linkSurface =
    shareState === undefined ? (
      <LoadingSurface />
    ) : shareState === null ? (
      <GenerateSurface onGenerate={handleGenerate} isMutating={isMutating} />
    ) : (
      <LinkSurface url={shareUrl ?? ""} copied={copied} onCopy={handleCopy} />
    );

  const revokeRow = shareState && (
    <div className="flex items-center justify-start gap-3">
      <Button
        variant="destructive"
        size="sm"
        onClick={handleRevokeAndUnfeature}
        disabled={isMutating}
        loading={isMutating}
      >
        {isMutating ? "Revoking link" : "Revoke link"}
      </Button>
      {featured.isFeatured && (
        <p className="text-xs text-muted-foreground">
          Revoking also removes this session from Featured Sessions.
        </p>
      )}
    </div>
  );

  if (!isOwner) {
    return (
      <div className="flex min-w-0 flex-col gap-3">
        {linkSurface}
        {revokeRow}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {linkSurface}

      <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">Featured Session</p>
          <p className="text-xs text-muted-foreground">
            Anyone in the project can view in the sidebar, read-only.
          </p>
        </div>
        <Switch
          checked={featured.isFeatured}
          onCheckedChange={handleFeaturedToggle}
          disabled={featured.isMutating || isMutating}
          aria-label={
            featured.isFeatured ? "Remove from Featured Sessions" : "Add to Featured Sessions"
          }
        />
      </div>

      {revokeRow}
    </div>
  );
};

const LoadingSurface = () => (
  <div
    className="flex h-11 items-center justify-center rounded-xl border bg-muted/30 text-muted-foreground"
    role="status"
    aria-live="polite"
  >
    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
  </div>
);

interface GenerateSurfaceProps {
  onGenerate: () => void;
  isMutating: boolean;
}

const GenerateSurface = ({ onGenerate, isMutating }: GenerateSurfaceProps) => (
  <button
    type="button"
    onClick={onGenerate}
    disabled={isMutating}
    className="flex h-11 w-full animate-in fade-in-50 items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 text-sm font-medium text-foreground transition-colors cursor-pointer hover:border-primary/40 hover:bg-muted/50 disabled:cursor-default disabled:opacity-60"
  >
    {isMutating ? (
      <Loader2 className="size-4 animate-spin text-primary" aria-hidden="true" />
    ) : (
      <Link2 className="size-4 text-primary" aria-hidden="true" />
    )}
    {isMutating ? "Creating link" : "Generate link"}
  </button>
);

interface LinkSurfaceProps {
  url: string;
  copied: boolean;
  onCopy: () => void;
}

const LinkSurface = ({ url, copied, onCopy }: LinkSurfaceProps) => (
  <div className="relative flex h-11 w-full min-w-0 animate-in fade-in-50 items-center overflow-hidden rounded-xl border bg-muted/30">
    <p className="min-w-0 select-all truncate pl-4 pr-11 font-mono text-xs text-foreground">
      {url}
    </p>
    <Button
      variant="ghost"
      size="icon"
      onClick={onCopy}
      aria-label={copied ? "Copied" : "Copy link"}
      className="absolute right-1.5 top-1/2 size-7 -translate-y-1/2"
    >
      {copied ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
    </Button>
  </div>
);
